import { AccountRole } from './account';
import { GroupSchedule, GroupScheduleType, GroupStatus } from './group';
import { UserRating } from './rating';

export type SocialLink = {
  type: 'twitter' | 'tiktok' | 'instagram' | 'facebook' | 'linkedin' | 'youtube' | 'url' | 'referral';
  url: string;
};

export type AudioGroupMembers = {
  name: string; // deprecated, replaced by nickname
  nickname: string;
  picture: string; // deprecated, replaced by imageUrl
  imageUrl: string;
  role?: string;
  id: string;
  info?: string;
  email?: string;
  tagline?: string;
  socialLinks?: SocialLink[];
  hasCommunities?: boolean;
  isGroupFacilitator?: boolean;
  readableId?: string;
};

export type AudioGroupMembersV2 = {
  id: string;
  name: string;
  picture: string;
  isGroupFacilitator?: boolean;
  tagline?: string;
  hasCommunities?: boolean;
  readableId?: string;
};

export type PostponeInfo = {
  status: boolean;
  dueDate: number;
};

export type SwitchGroupLeader = {
  switchedAt: number;
  originalGroupLeaderId: string;
};

export type GroupGraphicalIdentification = {
  smallImageUrl: string;
  largeImageUrl: string;
};

export enum GroupDiscussionStyle {
  PersonalGrowthCircle = 'personal_growth_circle',
  SkillsDevelopmentCircle = 'skills_development_circle',
  FacilitatedVentingCircle = 'facilitated_venting_circle',
}

/**
 * Contains data related to closing live session
 * in recurrent rooms.
 * closedAt and expectedSessionEnd are not used
 * added only for debug/log purposes or for future usages
 */
export type ClosedSession = {
  closedAt: number;
  expectedSessionEnd: number;
  // session date with 1h offset
  sessionEndDueDate: number;
};

export type AudioGroup = {
  id: string;
  title: string;
  description: string;
  picture: { default: string };
  startDate: string;
  endDate?: string;
  schedule: GroupSchedule;
  nextSession: number | null;
  leaderAccountId: string;
  leaderName: string;
  leaderNickname?: string;
  leaderPicture: string;
  leaderInfo: string;
  leaderEmail: string;
  leaderTagline: string;
  leaderCertifications: string;
  leaderSocialLinks: SocialLink[];
  leaderHasCommunities: boolean;
  leaderRole: AccountRole;
  leaderReadableId: string;
  leaderRating: UserRating | null;
  isGroupLeader: boolean;
  criterias: { segment: string[] };
  membersWithModeratorStatus?: AudioGroupMembers[];
  organizationName?: string;
  organizationDescription?: string;
  organizationLogo?: string;
  shareableDeepLink?: string;
  postponeInfo?: PostponeInfo;
  closedSession?: ClosedSession;
  switchGroupLeader?: SwitchGroupLeader | null;
  status?: GroupStatus;
  specificMeetings?: boolean;
  isPromoted: boolean;
  supportGroup: boolean;
  dateCreated?: Date;
  lastUpdated?: Date;
  paymentRequired?: boolean;
  lastSessionStartTime?: number;
  planLabels: { id: string; name: string }[];
  discussionStyle?: GroupDiscussionStyle | null;
};

export type AudioRoom = AudioGroup & {
  segment: string;
  moderators: AudioGroupMembers[];
  members?: AudioGroupMembers[];
  nextStart: number;
  nextEnd: number;
  isPeerRoomGuide: boolean;
  segmentsList: string[];
  chatChannelId: string;
  scheduleType?: GroupScheduleType;
  roomSID?: string;
  recordedInTwilio?: boolean;
  recordSessions?: boolean;
  setRecordSessionTimestamp?: number;
  graphicIdentification?: GroupGraphicalIdentification;
};

export type AudioRoomTokens = {
  token: string;
  meetingSocketToken: string;
};

export type MeetingAnalyzerInMeetingStatusReq = {
  accountId: string;
  isHost: boolean;
  name: string;
  status: string;
  userId: string;
};

export enum InMeetingStatus {
  JOINED = 'joined',
  LEFT = 'left',
}

export enum GroupSessionReaction {
  HEART = 'heart',
}

export enum GuideType {
  GroupFacilitators = 'GFs',
  Gusers = 'Gusers',
  Peers = 'Peers',
}

export enum AudioRoomProvider {
  Twilio = 'twilio',
  GetStream = 'getStream',
}
